/**
 * Textos das boas-vindas para quem vem da Classic (`textos/textos-pt-BR.json` do pacote do Claude
 * Design, 28/10/2026, usados como estão). O português fica como padrão no código (chaves `bv_*` em
 * src/i18n/locales); en, es e fr são o rascunho do pacote, para o eMiner, e a tradução final é do
 * time. Onde o pacote escreve "Máquina de Leads", aqui vai `{{product}}`, o nome da marca.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';

export const BOAS_VINDAS_PT = {
  dialog: 'Novidades da {{product}}',
  back: 'Voltar',
  counter: '{{n}} de {{total}}',
  sr_card: 'Cartão {{n}} de {{total}}: {{title}}',
  sr_closed: 'Apresentação fechada.',
  role_card: 'cartão',
  menu_title: 'O que há de novo',
  menu_sub: 'As novidades da versão nova',

  b_start: 'Começar',
  b_next: 'Próximo',
  b_continue: 'Continuar',
  b_open: 'Abrir o novo painel',

  ola_label: 'Bem-vindo de volta',
  ola_title: 'A {{product}} está de cara nova',
  ola_text: 'Redesenhamos tudo e trouxemos muita coisa nova. Veja em um minuto.',
  fontes_label: 'Fontes de busca',
  fontes_title: 'Mais de 500 novas fontes',
  fontes_text:
    'Telefones e emails em muito mais sites e redes sociais. E você pode incluir as suas.',
  dominios_label: 'Emails',
  dominios_title: 'Mais de 100 domínios de email',
  dominios_text: 'Escolha entre os mais usados ou digite o domínio que quiser.',
  tipos_label: 'Negócios locais',
  tipos_title: 'Vários tipos de negócio numa busca',
  tipos_text: 'Dentistas, clínicas e laboratórios, tudo de uma vez.',
  sem_label: 'Semelhantes',
  sem_title: 'Encontre negócios parecidos',
  sem_text: 'Escolha um negócio da sua lista e encontre outros como ele.',
  enriquecer_label: 'Enriquecer dados',
  enriquecer_title: 'Enriquecimento mais profundo',
  enriquecer_text: 'Procura em mais fontes, mais rápido e com mais inteligência.',
  explorer_label: 'LeadExplorer',
  explorer_title: 'LeadExplorer mais completo',
  explorer_text:
    'Digite um site ou CNPJ. Mais rápido, mais inteligente e com dados de mais fontes.',
  exportar_label: 'Exportar',
  exportar_title: 'Baixe a lista do seu jeito',
  exportar_text: 'Escolha as colunas e o formato. A lista sai pronta para usar.',
  fase_label: 'Em breve',
  fase_title: 'E isso é só o começo',
  fase_text: 'Uma nova fase, com mais inteligência e novos dados de mercado.',
  fim_title: 'Tudo pronto',
  fim_text: 'Suas listas, seus contatos e sua licença vieram junto.',
  fim_tip: 'Para rever, vá em Conta › O que há de novo.',

  // Dentro das ilustrações (aria-hidden).
  i_sources_n: 'novas fontes de busca',
  i_sources_card: 'Fontes da sua busca',
  i_sources_add: 'Incluir uma fonte',
  i_sources_add_help: 'Qualquer site que você quiser.',
  i_src_company_sites: 'Sites de empresas',
  i_src_directories: 'Diretórios',
  i_src_news: 'Sites de notícias',
  i_domains_n: 'domínios de email',
  i_domains_card: 'Ou o domínio que você quiser',
  i_domains_custom: 'Personalizado',
  i_type_1: 'dentista',
  i_type_2: 'clínica odontológica',
  i_type_3: 'laboratório',
  i_types_summary: '3 tipos de negócio numa busca só',
  i_from_list: 'da sua lista',
  i_lookalikes: '8 negócios parecidos',
  i_business_sub: 'Indústria metalúrgica · Curitiba',
  i_enrich: 'Enriquecer',
  i_searching: 'Procurando em mais fontes…',
  i_ready: 'Pronto · dados de {{n}} fontes',
  i_email: 'Email',
  i_site: 'Site',
  i_company_id: 'CNPJ',
  i_registry: 'Receita Federal',
  i_activity_code: 'CNAE 2511-0/00',
  i_analyze: 'Analisar',
  i_active: 'Ativa na Receita Federal',
  i_contacts: 'Contatos',
  i_contacts_sub: '1 telefone · 3 emails',
  i_activity: 'Atividade',
  i_activity_value: 'Estruturas metálicas',
  i_online_since: 'No ar desde',
  i_online_sub: 'site e domínio',
  i_summary: 'Resumo',
  i_summary_text:
    'Indústria metalúrgica de Curitiba que faz estruturas para galpões e coberturas. Atende construtoras da região.',
  i_download_title: 'Baixar a lista',
  i_download_sub: 'indústria · Curitiba · 42 negócios',
  i_columns: 'Colunas',
  i_col_name: 'Nome',
  i_col_phone: 'Telefone',
  i_col_address: 'Endereço',
  i_col_rating: 'Avaliação',
  i_only_mobile: 'Só celulares',
  i_format: 'Formato',
  i_download: 'Baixar',
  i_soon: 'Em breve',
  i_soon_1: 'Mais inteligência',
  i_soon_2: 'Novos dados de mercado',
  i_soon_3: 'Mais oportunidades',
} as const;

export type BoasVindasKey = keyof typeof BOAS_VINDAS_PT;

export function useBoasVindasText() {
  const { t } = useTranslation();
  const product = useBrand().appTitle;
  return useCallback(
    (key: BoasVindasKey, vars?: Record<string, string | number>): string =>
      t(`bv_${key}`, {
        defaultValue: BOAS_VINDAS_PT[key],
        product,
        ...vars,
        interpolation: { escapeValue: false },
      }) as string,
    [t, product],
  );
}
